import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { Sex, User, UserRole } from '../../types';
import { CreateUserData, generatePassword, isValidEmail } from './adminUtils';
import {
  composeDisplayName,
  splitDisplayName,
  formatPhoneDigitsInput,
  isValidRoPhoneDigits,
} from '../../utils/nameUtils';
import { COMMON_SPECIALTIES } from '../Profile/profileUtils';
import BirthDatePicker from '../common/BirthDatePicker';
import DialogHeading from '../common/DialogHeading';
import {
  dialogActionsSx,
  dialogContentSx,
  dialogPaperSx,
  dialogTitleSx,
} from '../common/dialogLayout';
import { t } from '../../utils/translations';

interface AdminUserDialogsProps {
  createDialogOpen: boolean;
  editDialogOpen: boolean;
  createUserLoading?: boolean;
  editUserLoading?: boolean;
  createUserData: CreateUserData;
  editUserData: Partial<CreateUserData>;
  selectedUser: User | null;
  currentUserRole?: UserRole;
  onCloseCreate: () => void;
  onCloseEdit: () => void;
  onCreateUserDataChange: (data: CreateUserData) => void;
  onEditUserDataChange: (data: Partial<CreateUserData>) => void;
  onCreateUser: () => void;
  onEditUser: () => void;
  onCopyCredentials: () => void;
}

function ProfileFields({
  data,
  disabled,
  onChange,
}: {
  data: Partial<CreateUserData>;
  disabled?: boolean;
  onChange: (patch: Partial<CreateUserData>) => void;
}) {
  const specialties = data.specialties ?? [];

  const toggleSpecialty = (spec: string) => {
    const next = specialties.includes(spec)
      ? specialties.filter((s) => s !== spec)
      : [...specialties, spec];
    onChange({ specialties: next });
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, color: 'text.secondary' }}>
        {t.admin.users.profileSection}
      </Typography>
      <TextField
        fullWidth
        label={t.admin.users.phoneNumber}
        value={data.phoneNumber ?? ''}
        onChange={(e) => onChange({ phoneNumber: formatPhoneDigitsInput(e.target.value) })}
        margin="normal"
        required
        disabled={disabled}
        helperText={t.admin.users.phoneHelper}
        inputProps={{ inputMode: 'numeric' }}
      />
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <FormControl fullWidth margin="normal" required disabled={disabled}>
          <InputLabel>{t.admin.users.sex}</InputLabel>
          <Select
            value={data.sex ?? ''}
            label={t.admin.users.sex}
            onChange={(e) => onChange({ sex: e.target.value as Sex | '' })}
          >
            <MenuItem value="feminin">{t.admin.users.sexFemale}</MenuItem>
            <MenuItem value="masculin">{t.admin.users.sexMale}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ width: '100%' }}>
          <BirthDatePicker
            label={t.admin.users.birthDate}
            value={data.birthDate ?? ''}
            onChange={(birthDate) => onChange({ birthDate })}
            margin="normal"
            required
            disabled={disabled}
          />
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 1.5, mb: 1, fontWeight: 600 }}>
        {t.admin.users.specialties} *
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {COMMON_SPECIALTIES.map((spec) => (
          <Chip
            key={spec}
            label={spec}
            size="small"
            color={specialties.includes(spec) ? 'primary' : 'default'}
            variant={specialties.includes(spec) ? 'filled' : 'outlined'}
            onClick={() => !disabled && toggleSpecialty(spec)}
            disabled={disabled}
          />
        ))}
      </Box>
    </>
  );
}

const AdminUserDialogs: React.FC<AdminUserDialogsProps> = ({
  createDialogOpen,
  editDialogOpen,
  createUserLoading = false,
  editUserLoading = false,
  createUserData,
  editUserData,
  selectedUser,
  currentUserRole,
  onCloseCreate,
  onCloseEdit,
  onCreateUserDataChange,
  onEditUserDataChange,
  onCreateUser,
  onEditUser,
  onCopyCredentials,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const emailTrimmed = createUserData.email.trim();
  const emailTouched = emailTrimmed.length > 0;
  const emailValid = isValidEmail(createUserData.email);

  const createProfileOk =
    isValidRoPhoneDigits(createUserData.phoneNumber) &&
    Boolean(createUserData.sex) &&
    Boolean(createUserData.birthDate) &&
    createUserData.specialties.length > 0;

  const canSubmitCreate =
    !createUserLoading &&
    Boolean(createUserData.firstName.trim()) &&
    Boolean(createUserData.lastName.trim()) &&
    Boolean(createUserData.password) &&
    emailValid &&
    createProfileOk;

  const updateCreateName = (patch: { firstName?: string; lastName?: string }) => {
    const firstName = patch.firstName ?? createUserData.firstName;
    const lastName = patch.lastName ?? createUserData.lastName;
    const fullName = composeDisplayName(firstName, lastName);
    onCreateUserDataChange({
      ...createUserData,
      firstName,
      lastName,
      fullName,
      password: generatePassword(fullName),
    });
  };

  const editFirstName =
    editUserData.firstName ?? splitDisplayName(editUserData.fullName || '').firstName;
  const editLastName =
    editUserData.lastName ?? splitDisplayName(editUserData.fullName || '').lastName;

  const editProfileOk =
    isValidRoPhoneDigits(editUserData.phoneNumber || '') &&
    Boolean(editUserData.sex) &&
    Boolean(editUserData.birthDate) &&
    (editUserData.specialties?.length ?? 0) > 0;

  const editSnapshot = JSON.stringify({
    firstName: editFirstName.trim(),
    lastName: editLastName.trim(),
    role: editUserData.role ?? '',
    phoneNumber: (editUserData.phoneNumber ?? '').replace(/\D/g, ''),
    sex: editUserData.sex ?? '',
    birthDate: editUserData.birthDate ?? '',
    specialties: [...(editUserData.specialties ?? [])].sort(),
  });

  // Snapshot taken while rendering (not in an effect) so the update button is
  // never briefly enabled right after the dialog opens.
  const editSessionKey = editDialogOpen ? selectedUser?.id ?? null : null;
  const [editBaseline, setEditBaseline] = React.useState<{
    key: string | null;
    snapshot: string;
  }>({ key: null, snapshot: '' });

  if (editBaseline.key !== editSessionKey) {
    setEditBaseline({ key: editSessionKey, snapshot: editSnapshot });
  }

  const editDirty =
    editBaseline.key === editSessionKey && editSnapshot !== editBaseline.snapshot;

  const canSubmitEdit =
    !editUserLoading &&
    editDirty &&
    Boolean(editFirstName.trim()) &&
    Boolean(editLastName.trim()) &&
    Boolean(editUserData.role) &&
    editProfileOk;

  const updateEditName = (patch: { firstName?: string; lastName?: string }) => {
    const firstName = patch.firstName ?? editFirstName;
    const lastName = patch.lastName ?? editLastName;
    onEditUserDataChange({
      ...editUserData,
      firstName,
      lastName,
      fullName: composeDisplayName(firstName, lastName),
    });
  };

  return (
    <>
      <Dialog
        open={createDialogOpen}
        onClose={(_, reason) => {
          if (createUserLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            onCloseCreate();
            return;
          }
          onCloseCreate();
        }}
        disableEscapeKeyDown={createUserLoading}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{ sx: dialogPaperSx(fullScreen) }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <DialogHeading
            title={t.admin.users.createNewUser}
            subtitle={t.admin.users.createUserSubtitle}
          />
        </DialogTitle>
        <DialogContent sx={{ ...dialogContentSx, position: 'relative' }}>
          <Box
            sx={{
              opacity: createUserLoading ? 0.45 : 1,
              pointerEvents: createUserLoading ? 'none' : 'auto',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              {t.admin.users.accountSection}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label={t.admin.users.lastName}
                value={createUserData.lastName}
                onChange={(e) => updateCreateName({ lastName: e.target.value })}
                margin="normal"
                required
                disabled={createUserLoading}
              />
              <TextField
                fullWidth
                label={t.admin.users.firstName}
                value={createUserData.firstName}
                onChange={(e) => updateCreateName({ firstName: e.target.value })}
                margin="normal"
                required
                disabled={createUserLoading}
              />
            </Box>
            <TextField
              fullWidth
              label={t.admin.users.email}
              type="email"
              value={createUserData.email}
              onChange={(e) =>
                onCreateUserDataChange({ ...createUserData, email: e.target.value })
              }
              margin="normal"
              required
              disabled={createUserLoading}
              error={emailTouched && !emailValid}
              helperText={
                emailTouched && !emailValid ? t.admin.users.emailInvalid : undefined
              }
            />
            <TextField
              fullWidth
              label={t.admin.users.generatedPassword}
              value={createUserData.password}
              margin="normal"
              InputProps={{ readOnly: true }}
              helperText={t.admin.users.passwordHelperText}
              disabled={createUserLoading}
            />
            <FormControl fullWidth margin="normal" required disabled={createUserLoading}>
              <InputLabel>{t.admin.users.role}</InputLabel>
              <Select
                value={createUserData.role}
                onChange={(e) =>
                  onCreateUserDataChange({
                    ...createUserData,
                    role: e.target.value as UserRole,
                  })
                }
                label={t.admin.users.role}
              >
                <MenuItem value="counselor">{t.roles.counselor}</MenuItem>
                {currentUserRole === 'leader' && (
                  <MenuItem value="admin">{t.roles.admin}</MenuItem>
                )}
                {currentUserRole === 'leader' && (
                  <MenuItem value="leader">{t.roles.leader}</MenuItem>
                )}
              </Select>
            </FormControl>

            <ProfileFields
              data={createUserData}
              disabled={createUserLoading}
              onChange={(patch) =>
                onCreateUserDataChange({ ...createUserData, ...patch })
              }
            />
          </Box>

          {createUserLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255,255,255,0.72)',
                zIndex: 1,
              }}
              role="status"
              aria-live="polite"
            >
              <CircularProgress size={36} sx={{ color: '#C99700' }} />
              <Typography variant="body2" color="text.secondary">
                {t.admin.users.createUserInProgress}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={onCloseCreate} disabled={createUserLoading} variant="outlined" color="inherit">
            {t.common.cancel}
          </Button>
          <Button
            onClick={onCopyCredentials}
            variant="outlined"
            startIcon={<ContentCopy />}
            disabled={createUserLoading || !emailValid || !createUserData.password}
          >
            {t.admin.users.copyCredentials}
          </Button>
          <Button
            onClick={onCreateUser}
            variant="contained"
            disableElevation
            disabled={!canSubmitCreate}
            startIcon={
              createUserLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t.admin.users.createUser}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={(_, reason) => {
          if (editUserLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            onCloseEdit();
            return;
          }
          onCloseEdit();
        }}
        disableEscapeKeyDown={editUserLoading}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{ sx: dialogPaperSx(fullScreen) }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <DialogHeading
            title={t.admin.users.editUser}
            subtitle={selectedUser?.email ?? t.admin.users.editUserSubtitle}
          />
        </DialogTitle>
        <DialogContent sx={dialogContentSx}>
          <Box
            sx={{
              opacity: editUserLoading ? 0.45 : 1,
              pointerEvents: editUserLoading ? 'none' : 'auto',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label={t.admin.users.lastName}
                value={editLastName}
                onChange={(e) => updateEditName({ lastName: e.target.value })}
                margin="normal"
                required
                disabled={editUserLoading}
              />
              <TextField
                fullWidth
                label={t.admin.users.firstName}
                value={editFirstName}
                onChange={(e) => updateEditName({ firstName: e.target.value })}
                margin="normal"
                required
                disabled={editUserLoading}
              />
            </Box>
            <FormControl fullWidth margin="normal" required disabled={editUserLoading}>
              <InputLabel>{t.admin.users.role}</InputLabel>
              <Select
                value={editUserData.role || 'counselor'}
                onChange={(e) =>
                  onEditUserDataChange({
                    ...editUserData,
                    role: e.target.value as UserRole,
                  })
                }
                label={t.admin.users.role}
                disabled={
                  editUserLoading ||
                  (currentUserRole === 'admin' && selectedUser?.role === 'leader') ||
                  selectedUser?.isSupremeLeader === true
                }
              >
                <MenuItem value="counselor">{t.roles.counselor}</MenuItem>
                {currentUserRole === 'leader' && (
                  <MenuItem value="admin">{t.roles.admin}</MenuItem>
                )}
                {currentUserRole === 'leader' && (
                  <MenuItem value="leader">{t.roles.leader}</MenuItem>
                )}
              </Select>
            </FormControl>
            {currentUserRole === 'admin' && selectedUser?.role === 'leader' && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {t.admin.users.adminsCannotModifyLeaders}
              </Typography>
            )}

            <ProfileFields
              data={editUserData}
              disabled={editUserLoading}
              onChange={(patch) => onEditUserDataChange({ ...editUserData, ...patch })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={onCloseEdit} disabled={editUserLoading} variant="outlined" color="inherit">
            {t.common.cancel}
          </Button>
          <Button
            onClick={onEditUser}
            variant="contained"
            disableElevation
            disabled={!canSubmitEdit}
            startIcon={
              editUserLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t.admin.users.updateUser}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminUserDialogs;
